
import axios from 'axios';
import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';


/**
 * Secure Surgical Case Service
 * Uses backend API endpoints instead of direct Firestore access
 */
export const surgicalCaseService = {
  /**
   * Start a surgical case by linking an order to surgical phases
   * @param {string} orderId - The procedure/order ID
   * @param {Object} orderData - The order data
   * @param {string} userId - The user who started the case (optional - backend will use token)
   * @returns {Promise<string>} Promise resolving to the surgical case ID
   */
  startSurgicalCase: async (orderId, orderData, userId = null) => {
    try {
      console.log('Starting surgical case for order via API:', orderId);
      const token = await getAuthToken();
      
      // Validate required fields
      if (!orderId) {
        throw new Error('Order ID is required');
      }
      if (!orderData.patientId) {
        throw new Error('Patient ID is required');
      }

      const response = await axios.post(
        `${API_BASE}/surgical/surgical-cases`,
        { orderId, orderData },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Created surgical case with ID:', response.data.surgicalCaseId);
      return response.data.surgicalCaseId;
    } catch (error) {
      console.error('Error starting surgical case:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to start surgical case');
    }
  },

  /**
   * Check if a surgical case exists for an order
   * @param {string} orderId - The procedure/order ID
   * @returns {Promise<Object|null>} Promise resolving to case data or null
   */
  getSurgicalCase: async (orderId) => {
    try {
      console.log('Fetching surgical case via API:', orderId);
      const token = await getAuthToken();
      
      const response = await axios.get(
        `${API_BASE}/surgical/surgical-cases/order/${orderId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.surgicalCase;
    } catch (error) {
      console.error('Error fetching surgical case:', error);
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      return null;
    }
  },

  /**
   * Update a surgical case phase
   * @param {string} caseId - The surgical case ID
   * @param {string} phase - The phase to update (preOp, intraOp, pacu, postOp)
   * @param {Object} phaseData - The phase data to update
   * @returns {Promise<void>}
   */
  updateCasePhase: async (caseId, phase, phaseData) => {
    try {
      console.log('Updating surgical case phase via API:', caseId, phase, phaseData);
      const token = await getAuthToken();
      
      await axios.put(
        `${API_BASE}/surgical/surgical-cases/${caseId}/phase`,
        { phase, phaseData },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Updated surgical case phase:', caseId, phase);
    } catch (error) {
      console.error('Error updating surgical case phase:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to update surgical case phase');
    }
  },

  /**
   * Get all surgical cases for a facility
   * @param {string} facilityId - The facility ID
   * @param {Object} options - Additional options (page, limit)
   * @returns {Promise<Array>} Promise resolving to array of surgical cases
   */
  getSurgicalCasesForFacility: async (facilityId, options = {}) => {
    try {
      console.log('Fetching surgical cases for facility via API:', facilityId);
      const token = await getAuthToken();
      const { page = 1, limit = 50 } = options;
      
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      
      const response = await axios.get(
        `${API_BASE}/surgical/surgical-cases/facility/${facilityId}?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Fetched surgical cases:', response.data.surgicalCases.length);
      return response.data.surgicalCases;
    } catch (error) {
      console.error('Error fetching surgical cases:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to fetch surgical cases');
    }
  },

  /**
   * Update the overall status of a surgical case
   * @param {string} caseId - The surgical case ID
   * @param {string} status - The new status
   * @param {string} phase - The current phase (optional)
   * @returns {Promise<void>}
   */
  updateCaseStatus: async (caseId, status, phase = null) => {
    try {
      console.log('Updating surgical case status via API:', caseId, status, phase);
      const token = await getAuthToken();
      
      const payload = { status };
      if (phase) {
        payload.phase = phase;
      }
      
      await axios.put(
        `${API_BASE}/surgical/surgical-cases/${caseId}/status`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Updated surgical case status:', caseId, status);
    } catch (error) {
      console.error('Error updating surgical case status:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to update surgical case status');
    }
  },

  // Additional helper methods for enhanced functionality

  /**
   * Get surgical case by ID
   * @param {string} caseId - The surgical case ID
   * @returns {Promise<Object|null>} Promise resolving to case data or null
   */
  getSurgicalCaseById: async (caseId) => {
    try {
      console.log('Fetching surgical case by ID via API:', caseId);
      const token = await getAuthToken();
      
      const response = await axios.get(
        `${API_BASE}/surgical/surgical-cases/${caseId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.surgicalCase;
    } catch (error) {
      console.error('Error fetching surgical case by ID:', error);
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  },

  /**
   * Get surgical cases with pagination and filtering
   * @param {string} facilityId - The facility ID
   * @param {Object} filters - Filtering options
   * @param {Object} pagination - Pagination options
   * @returns {Promise<Object>} Promise resolving to paginated results
   */
  getSurgicalCasesPaginated: async (facilityId, filters = {}, pagination = {}) => {
    try {
      console.log('Fetching paginated surgical cases via API:', facilityId, filters);
      const token = await getAuthToken();
      const { page = 1, limit = 50 } = pagination;
      
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      
      // Add filter parameters
      if (filters.status) {
        params.append('status', filters.status);
      }
      if (filters.phase) {
        params.append('phase', filters.phase);
      }
      if (filters.patientId) {
        params.append('patientId', filters.patientId);
      }
      if (filters.procedureType) {
        params.append('procedureType', filters.procedureType);
      }
      if (filters.startDate) {
        params.append('startDate', filters.startDate);
      }
      if (filters.endDate) {
        params.append('endDate', filters.endDate);
      }
      
      const response = await axios.get(
        `${API_BASE}/surgical/surgical-cases/facility/${facilityId}?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return {
        surgicalCases: response.data.surgicalCases,
        pagination: response.data.pagination
      };
    } catch (error) {
      console.error('Error fetching paginated surgical cases:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  },

  /**
   * Search surgical cases by criteria
   * @param {string} facilityId - The facility ID
   * @param {string} searchTerm - Search term
   * @param {Object} filters - Additional filters
   * @returns {Promise<Array>} Array of matching surgical cases
   */
  searchSurgicalCases: async (facilityId, searchTerm, filters = {}) => {
    try {
      console.log('Searching surgical cases via API:', facilityId, searchTerm);
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      const response = await axios.get(
        `${API_BASE}/surgical/surgical-cases/facility/${facilityId}/search?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.surgicalCases;
    } catch (error) {
      // If search endpoint doesn't exist, fallback to getting all and filtering
      try {
        const allCases = await surgicalCaseService.getSurgicalCasesForFacility(facilityId);
        
        if (!searchTerm || searchTerm.trim() === '') {
          return allCases;
        }
        
        const lowerSearchTerm = searchTerm.toLowerCase();
        return allCases.filter(surgicalCase => 
          (surgicalCase.patientName && surgicalCase.patientName.toLowerCase().includes(lowerSearchTerm)) ||
          (surgicalCase.patientMRN && surgicalCase.patientMRN.toLowerCase().includes(lowerSearchTerm)) ||
          (surgicalCase.procedureType && surgicalCase.procedureType.toLowerCase().includes(lowerSearchTerm)) ||
          (surgicalCase.orderingProvider && surgicalCase.orderingProvider.toLowerCase().includes(lowerSearchTerm)) ||
          (surgicalCase.status && surgicalCase.status.toLowerCase().includes(lowerSearchTerm))
        );
      } catch (fallbackError) {
        console.error('Error searching surgical cases (fallback):', fallbackError);
        throw error;
      }
    }
  },

  /**
   * Get surgical cases by status for a facility
   * @param {string} facilityId - The facility ID
   * @param {string} status - Case status
   * @returns {Promise<Array>} Array of surgical cases with the specified status
   */
  getSurgicalCasesByStatus: async (facilityId, status) => {
    try {
      console.log('Getting surgical cases by status via API:', facilityId, status);
      
      return await surgicalCaseService.getSurgicalCasesPaginated(facilityId, { status });
    } catch (error) {
      console.error('Error getting surgical cases by status:', error);
      throw error;
    }
  },

  /**
   * Get active surgical cases for a facility
   * @param {string} facilityId - The facility ID
   * @returns {Promise<Array>} Array of active surgical cases
   */
  getActiveSurgicalCases: async (facilityId) => {
    try {
      console.log('Getting active surgical cases via API:', facilityId);
      
      const activeCases = await Promise.all([
        surgicalCaseService.getSurgicalCasesByStatus(facilityId, 'started'),
        surgicalCaseService.getSurgicalCasesByStatus(facilityId, 'in-progress'),
        surgicalCaseService.getSurgicalCasesByStatus(facilityId, 'pacu')
      ]);
      
      return activeCases.flat();
    } catch (error) {
      console.error('Error getting active surgical cases:', error);
      throw error;
    }
  },

  /**
   * Get surgical case statistics for a facility
   * @param {string} facilityId - The facility ID
   * @param {Object} dateRange - Date range for statistics
   * @returns {Promise<Object>} Surgical case statistics
   */
  getSurgicalCaseStats: async (facilityId, dateRange = {}) => {
    try {
      console.log('Getting surgical case statistics via API:', facilityId);
      
      // Get all cases for the facility and calculate stats
      const allCases = await surgicalCaseService.getSurgicalCasesForFacility(facilityId);
      
      // Filter by date range if provided
      let filteredCases = allCases;
      if (dateRange.startDate || dateRange.endDate) {
        filteredCases = allCases.filter(case_ => {
          const caseDate = new Date(case_.startedAt || case_.createdAt);
          if (dateRange.startDate && caseDate < new Date(dateRange.startDate)) return false;
          if (dateRange.endDate && caseDate > new Date(dateRange.endDate)) return false;
          return true;
        });
      }
      
      return {
        totalCases: filteredCases.length,
        activeCases: filteredCases.filter(c => ['started', 'in-progress', 'pacu'].includes(c.status)).length,
        completedCases: filteredCases.filter(c => c.status === 'completed').length,
        cancelledCases: filteredCases.filter(c => c.status === 'cancelled').length,
        byPhase: {
          preOp: filteredCases.filter(c => c.phase === 'pre-op').length,
          intraOp: filteredCases.filter(c => c.phase === 'intra-op').length,
          pacu: filteredCases.filter(c => c.phase === 'pacu').length,
          postOp: filteredCases.filter(c => c.phase === 'post-op').length
        },
        averageDuration: null, // Would need additional calculation
        period: dateRange
      };
    } catch (error) {
      console.error('Error getting surgical case stats:', error);
      throw error;
    }
  },

  /**
   * Export surgical case data
   * @param {string} facilityId - The facility ID
   * @param {Object} filters - Export filters
   * @param {string} format - Export format ('json' or 'csv')
   * @returns {Promise<string>} Exported data
   */
  exportSurgicalCases: async (facilityId, filters = {}, format = 'json') => {
    try {
      console.log('Exporting surgical cases via API:', facilityId, filters, format);
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      params.append('format', format);
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      const response = await axios.get(
        `${API_BASE}/surgical/surgical-cases/facility/${facilityId}/export?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (format === 'json') {
        return JSON.stringify(response.data.surgicalCases, null, 2);
      } else {
        return response.data.exportData;
      }
    } catch (error) {
      console.error('Error exporting surgical cases:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  },

  /**
   * Update multiple phases of a surgical case
   * @param {string} caseId - The surgical case ID
   * @param {Object} phaseUpdates - Object with phase updates
   * @returns {Promise<void>}
   */
  updateMultipleCasePhases: async (caseId, phaseUpdates) => {
    try {
      console.log('Updating multiple case phases via API:', caseId, phaseUpdates);
      
      const updatePromises = Object.entries(phaseUpdates).map(([phase, phaseData]) =>
        surgicalCaseService.updateCasePhase(caseId, phase, phaseData)
      );
      
      await Promise.all(updatePromises);
      
      console.log('Updated multiple case phases:', caseId);
    } catch (error) {
      console.error('Error updating multiple case phases:', error);
      throw error;
    }
  },

  /**
   * Start next phase of a surgical case
   * @param {string} caseId - The surgical case ID
   * @param {string} nextPhase - The next phase to start
   * @returns {Promise<void>}
   */
  startNextPhase: async (caseId, nextPhase) => {
    try {
      console.log('Starting next phase via API:', caseId, nextPhase);
      
      // Update the current phase to completed and start the next phase
      const currentCase = await surgicalCaseService.getSurgicalCaseById(caseId);
      if (!currentCase) {
        throw new Error('Surgical case not found');
      }
      
      const currentPhase = currentCase.phase;
      const now = new Date().toISOString();
      
      // Update current phase to completed
      if (currentPhase && currentCase.phases[currentPhase]) {
        await surgicalCaseService.updateCasePhase(caseId, currentPhase, {
          ...currentCase.phases[currentPhase],
          status: 'completed',
          completedAt: now
        });
      }
      
      // Start next phase
      await surgicalCaseService.updateCasePhase(caseId, nextPhase, {
        status: 'in-progress',
        startedAt: now,
        completedAt: null,
        checklist: []
      });
      
      // Update overall case status and phase
      await surgicalCaseService.updateCaseStatus(caseId, 'in-progress', nextPhase);
      
      console.log('Started next phase:', caseId, nextPhase);
    } catch (error) {
      console.error('Error starting next phase:', error);
      throw error;
    }
  },

  /**
   * Complete a surgical case
   * @param {string} caseId - The surgical case ID
   * @param {Object} completionData - Completion data
   * @returns {Promise<void>}
   */
  completeSurgicalCase: async (caseId, completionData = {}) => {
    try {
      console.log('Completing surgical case via API:', caseId);
      
      const currentCase = await surgicalCaseService.getSurgicalCaseById(caseId);
      if (!currentCase) {
        throw new Error('Surgical case not found');
      }
      
      const currentPhase = currentCase.phase;
      const now = new Date().toISOString();
      
      // Complete current phase
      if (currentPhase && currentCase.phases[currentPhase]) {
        await surgicalCaseService.updateCasePhase(caseId, currentPhase, {
          ...currentCase.phases[currentPhase],
          status: 'completed',
          completedAt: now
        });
      }
      
      // Update case status to completed
      await surgicalCaseService.updateCaseStatus(caseId, 'completed');
      
      console.log('Completed surgical case:', caseId);
    } catch (error) {
      console.error('Error completing surgical case:', error);
      throw error;
    }
  }
};
